import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
var __jsx = React.createElement;
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PROMO_STORAGE_KEY } from '../utils/promoConstants';
import { isOnPromoPost } from '../utils/promoUtils';
/**
 * Calculate eligibility reason for DEBUG purposes only
 * This mirrors the production logic but adds reason text for debugging
 */
var calculateDebugReason = function calculateDebugReason(promoCode, dropOffState, error, currentPath) {
  var isOnMatchingPost = isOnPromoPost(currentPath, dropOffState.post_id);
  var isPromoVisible = dropOffState.promocode_visible;
  var hasDroppedOff = dropOffState.hasDroppedOff;
  var isExpired = promoCode ? new Date() >= promoCode.expires_at : false;

  // Check conditions in same order as production logic
  if (error) {
    return "Error: ".concat(error);
  } else if (!promoCode) {
    return "No promo code available";
  } else if (isExpired) {
    return "Promo code expired";
  } else if (dropOffState.closed_at) {
    return "Promo was closed by user";
  } else if (promoCode.closed_at) {
    return "Promo was already shown/closed";
  } else if (isPromoVisible && hasDroppedOff) {
    return isOnMatchingPost ? "On promo post after drop-off (button mode)" : "Drop-off recovery mode (popup mode)";
  } else if (isOnMatchingPost) {
    return "User is on promo post page";
  } else {
    return "Not eligible - no matching conditions";
  }
};

/**
 * Debug Information Component for Promo System
 * 
 * Provides a collapsible debug panel showing:
 * - Current promo state and eligibility
 * - Drop-off tracking status
 * - Display configuration
 * - Delay timing information
 * - Control buttons for testing
 */
var PromoDebugInfo = function PromoDebugInfo(_ref) {
  var eligibilityState = _ref.eligibilityState,
    dropOffState = _ref.dropOffState,
    promoCode = _ref.promoCode,
    shouldShowBanner = _ref.shouldShowBanner,
    displayConfig = _ref.displayConfig,
    isPopupOpen = _ref.isPopupOpen,
    isExpired = _ref.isExpired,
    shouldShowWithDelay = _ref.shouldShowWithDelay,
    isDelayActive = _ref.isDelayActive,
    remainingDelay = _ref.remainingDelay,
    resetPromoVisibility = _ref.resetPromoVisibility,
    setIsPopupOpen = _ref.setIsPopupOpen,
    error = _ref.error;
  var _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  var location = useLocation();

  // Calculate debug reason only when component is rendered
  var debugReason = calculateDebugReason(promoCode, dropOffState, error, location.pathname);
  return /*#__PURE__*/React.createElement("div", {
    className: "fixed top-15 left-4 bg-black text-white text-xs rounded-lg z-50 max-w-sm font-mono shadow-xl border border-gray-600",
    style: {
      zIndex: '99999999'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between p-2 bg-gray-800 rounded-t-lg cursor-pointer hover:bg-gray-700 transition-colors",
    onClick: function onClick() {
      return setIsExpanded(!isExpanded);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "font-bold text-blue-300 flex items-center space-x-2"
  }, /*#__PURE__*/React.createElement("span", null, "\uD83C\uDF0D"), isExpanded && /*#__PURE__*/React.createElement("span", null, "Promo Debug")), /*#__PURE__*/React.createElement("div", {
    className: "transform transition-transform duration-200 ".concat(isExpanded ? 'rotate-180' : '')
  }, /*#__PURE__*/React.createElement("svg", {
    width: "12",
    height: "12",
    viewBox: "0 0 24 24",
    fill: "currentColor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M7 10l5 5 5-5z"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "overflow-hidden transition-all duration-300 ease-in-out ".concat(isExpanded ? 'max-h-[1000px] opacity-100 w-[300px]' : 'max-h-0 w-5 opacity-0')
  }, /*#__PURE__*/React.createElement("div", {
    className: "p-3 space-y-1 max-h-80 overflow-y-auto"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-green-300 font-bold text-lg "
  }, "\uD83D\uDCCA Current State:"), /*#__PURE__*/React.createElement("div", null, "Reason: ", debugReason), /*#__PURE__*/React.createElement("div", null, "Current: ", location.pathname), /*#__PURE__*/React.createElement("div", null, "Previous: ", dropOffState.lastPath), /*#__PURE__*/React.createElement("div", null, "Has Promo: ", promoCode ? '✅' : '❌'), /*#__PURE__*/React.createElement("div", null, "Eligible: ", eligibilityState.isEligible ? '✅' : '❌'), /*#__PURE__*/React.createElement("div", null, "Should Show: ", shouldShowBanner ? '✅' : '❌'), /*#__PURE__*/React.createElement("div", null, "Mode: ", displayConfig.showPopup ? 'Popup' : displayConfig.showButton ? 'Button' : 'None'), /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-600 pt-2 mt-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-orange-300 text-lg font-bold"
  }, "\uD83C\uDFAF Drop-off Status:"), /*#__PURE__*/React.createElement("div", null, "Drop-off Visible: ", dropOffState.promocode_visible ? '✅' : '❌'), /*#__PURE__*/React.createElement("div", null, "Has Dropped Off: ", dropOffState.hasDroppedOff ? '✅' : '❌'), /*#__PURE__*/React.createElement("div", null, "First Time: ", dropOffState.first_time_visible ? '✅' : '❌'), /*#__PURE__*/React.createElement("div", null, "Popup Open: ", isPopupOpen ? '✅' : '❌'), /*#__PURE__*/React.createElement("div", null, "Expired: ", isExpired ? '✅' : '❌')), /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-600 pt-2 mt-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-purple-300 font-bold text-lg "
  }, "\uD83C\uDFAB Promo Info:"), /*#__PURE__*/React.createElement("div", {
    className: "text-yellow-300 text-xs"
  }, "\u23F0 Expires: ", promoCode ? new Date(promoCode.expires_at).toLocaleString() : 'N/A'), promoCode && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "text-xs"
  }, "Code: ", promoCode.code), /*#__PURE__*/React.createElement("div", {
    className: "text-xs"
  }, "Post ID: ", promoCode.post_id), /*#__PURE__*/React.createElement("div", {
    className: "text-xs"
  }, "Discount: ", promoCode.discount_amount))), /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-600 pt-2 mt-2 space-y-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-red-300 font-bold"
  }, "\uD83C\uDFAE Controls:"), /*#__PURE__*/React.createElement("button", {
    onClick: resetPromoVisibility,
    className: "block w-full px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
  }, "Reset State"), /*#__PURE__*/React.createElement("button", {
    onClick: function onClick() {
      return setIsPopupOpen(true);
    },
    className: "block w-full px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors",
    disabled: !promoCode
  }, "Force Popup"), /*#__PURE__*/React.createElement("button", {
    onClick: function onClick() {
      var testState = _objectSpread(_objectSpread({}, dropOffState), {}, {
        hasDroppedOff: true,
        dropOffTime: Date.now(),
        lastPath: location.pathname,
        promocode_visible: true,
        closed_at: null,
        first_time_visible: true
      });
      localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(testState));
      window.location.reload();
    },
    className: "block w-full px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
  }, "Test Drop-off (5s)"), /*#__PURE__*/React.createElement("button", {
    onClick: function onClick() {
      var testState = _objectSpread(_objectSpread({}, dropOffState), {}, {
        hasDroppedOff: true,
        dropOffTime: Date.now(),
        lastPath: location.pathname,
        promocode_visible: true,
        closed_at: null,
        first_time_visible: false
      });
      localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(testState));
      window.location.reload();
    },
    className: "block w-full px-2 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-colors"
  }, "Test Drop-off (Instant)")))));
};
export default PromoDebugInfo;